<template>
	<div>
		<v-row>
			<!-- company-name -->
			<v-col lg="3" md="4" sm="6" cols="12" class="px-1">
				<v-text-field
					v-model="companyName"
					:label="$t('inputs.company-name')"
					dense
					outlined
					hide-details
					readonly
				></v-text-field>
			</v-col>
			
			<!-- landline -->
			<v-col lg="3" md="4" sm="6" cols="12" class="px-1">
				<v-text-field
					v-model="phone"
					:label="$t('inputs.phone')"
					dense
					outlined
					hide-details
					readonly
				></v-text-field>
			</v-col>

			<!-- start-work-time -->
			<v-col lg="2" md="3" sm="4" cols="12" class="px-1">
				<v-menu
					ref="startTimeMenu"
					v-model="startTimeMenu"
					:disabled="loading"
					:close-on-content-click="false"
					:return-value.sync="startTime"
					transition="scale-transition"
					offset-y
					max-width="200"
					min-width="200"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-text-field
							:value="startTime"
							:label="$t('inputs.start-work-time')"
							prepend-inner-icon="mdi-clock-time-four-outline"
							readonly
							outlined
							dense
							persistent-hint
							hide-details="auto"
							:disabled="loading"
							v-bind="attrs"
							v-on="on"
						></v-text-field>
					</template>
					<v-time-picker
						v-if="startTimeMenu"
						v-model="startTime"
						scrollable
						full-width
						@click:minute="$refs.startTimeMenu.save(startTime)"
					></v-time-picker>
				</v-menu>
			</v-col>
			
			<!-- end-work-time -->
			<v-col lg="2" md="3" sm="4" cols="12" class="px-1">
				<v-menu
					ref="endTimeMenu"
					v-model="endTimeMenu"
					:disabled="loading"
					:close-on-content-click="false"
					:return-value.sync="endTime"
					transition="scale-transition"
					offset-y
					max-width="200"
					min-width="200"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-text-field
							:value="endTime"
							:label="$t('inputs.end-work-time')"
							prepend-inner-icon="mdi-clock-time-four-outline"
							readonly
							outlined
							dense
							persistent-hint
							hide-details="auto"
							:disabled="loading"
							v-bind="attrs"
							v-on="on"
						></v-text-field>
					</template>
					<v-time-picker
						v-if="endTimeMenu"
						v-model="endTime"
						scrollable
						full-width
						@click:minute="$refs.endTimeMenu.save(endTime)"
					></v-time-picker>
				</v-menu>
			</v-col>
		</v-row>

		<!-- GPS data -->
		<p class="text-body-1 my-2">{{$t('CompanyInfo.gps-data')}}</p>
		
		<v-form ref="form">
			<v-row>
				<!-- baseUrl -->
				<v-col lg="3" md="4" sm="6" cols="12" class="px-1 pb-md-0">
					<v-text-field
						v-model="baseUrl"
						:label="$t('inputs.baseUrl')"
						outlined
						dense
						hide-details
						:disabled="loading"
						:rules="rules.url"
					></v-text-field>
				</v-col>

				<!-- gps-email -->
				<v-col lg="3" md="4" sm="6" cols="12" class="px-1 pb-md-0">
					<v-text-field
						v-model="email"
						:label="$t('inputs.gps-email')"
						outlined
						dense
						hide-details
						:disabled="loading"
						:rules="rules.email"
					></v-text-field>
				</v-col>

				<!-- gps-password -->
				<v-col lg="3" md="4" sm="6" cols="12" class="px-1 pb-md-0">
					<v-text-field
						v-model="password"
						:label="$t('inputs.gps-password')"
						outlined
						dense
						:hint="$t('messages.writing-new-password-replace-old')"
						persistent-hint
						hide-details="auto"
						:disabled="loading"
					></v-text-field>
				</v-col>
			</v-row>
		</v-form>

		<!-- map -->
		<p class="my-2">{{$t('CompanyInfo.location')}}</p>
		<div :style="{ height: `${$vuetify.breakpoint.height - 318}px` }">
			<v-map
				ref="map"
				:points.sync="points"
				:context-menu-items="contextMenuItems"
				:loading="loading"
				:class="{'error-map': isValidated}"
				@context-menu-select="setMarker"
			/>
		</div>

		<v-btn
			color="green white--text"
			class="mt-2"
			:loading="loading"
			@click="save"
		>
			{{$t('save')}}
		</v-btn>
	</div>
</template>

<script>
import rules from '@/helpers/validation rules';
import { mapState } from 'vuex';
import moment from 'moment';
export default {
	name: 'CompanyInfo',

	data: () => ({
		isValidated: false,
		loading: false,
		
		rules,

		startTimeMenu: false,
		endTimeMenu: false,
		
		// submit
		companyName: null,
		phone: null,
		baseUrl: null,
		email: null,
		password: null,
		startTime: null,
		endTime: null,
		points: [],
	}),

	computed: {
		...mapState({
			companyInfo: state => state.companyInfo.companyInfo
		}),

		contextMenuItems() {
			return [
				{
					text: this.$t('select'),
					action: 'select',
					icon: 'mdi-map-marker-radius',
					iconColor: 'success darken-2',
					class: 'success--text text--darken-2'
				}
			]
		}
	},

	methods: {
		save() {
			this.isValidated = this.points.length === 0;
			if (this.$refs.form.validate() && this.points.length) {
				this.loading = true;
				this.$store.dispatch('companyInfo/update', {
					startHour: `${moment().format('YYYY-MM-DD')}T${this.startTime}:00`,
					endHour: `${moment().format('YYYY-MM-DD')}T${this.endTime}:00`,
					baseUrl: this.baseUrl,
					email: this.email,
					password: this.password,
					point: this.points[0].latLng
				}).then(() => {
					// update companyInfo data
					this.$store.dispatch('companyInfo/fetchAll').then(() => {
						this.$eventBus.$emit('show-snackbar', 'info', this.$t('messages.saved-successfully'));
					}).finally(() => {
						this.loading = false;
						this.isValidated = false;
					});
				}).catch(() => {
					this.loading = false;
				})
			}
		},

		setMarker(coordinates) {
			this.isValidated = false;
			this.points = [];
			this.points.push({
				latLng: coordinates,
				icon: {
					size: [44, 44],
					anchor: [22, 44],
					url: require('@/assets/imgs/star.png')
				},
				draggable: true
			});
		},
	},

	mounted() {
		this.companyName = this.companyInfo.SchoolName;
		this.phone = this.companyInfo.Phone;
		this.baseUrl = this.companyInfo.BaseUrl;
		this.email = this.companyInfo.Email;
		this.startTime = moment(this.companyInfo.StartHour).format('hh:mm');
		this.endTime = moment(this.companyInfo.EndHour).format('hh:mm');

		if (this.companyInfo.SchoolLocation.Lat && this.companyInfo.SchoolLocation.Lng) {
			const point = {
				lat: this.companyInfo.SchoolLocation.Lat,
				lng: this.companyInfo.SchoolLocation.Lng
			};
			this.setMarker(point);
			this.$refs.map.setViewTo(point);
		}
	}
}
</script>

<style lang="scss">
#app .error-map .v-map__wrapper {
	border: 2px solid red;
}
</style>